<template>
  <div class="auth-shell d-flex justify-content-center align-items-center">
    <div v-if="showNotification" class="notification">
      {{ $t('registrationForm.success') }}
    </div>

    <div class="card p-4 auth-card wide">
      <div class="auth-head">
        <router-link
            to="/select-company"
            class="btn-outline back-btn"
            :aria-label="$t('registrationForm.backAria')"
        >
            <i class="bi bi-arrow-left"></i> {{ $t('registrationForm.back') }}
        </router-link>

        <h2 class="text-center mb-0 flex-1">{{ $t('registrationForm.title') }}</h2>

        <!-- spacer to balance flex just like ChangePassword.vue -->
        <span class="back-spacer" aria-hidden="true"></span>
    </div>

      <h5 class="section-title mt-4">{{ $t('registrationForm.companyDetails') }}</h5>
      <hr class="section-rule" />

      <form @submit.prevent="register" novalidate>
        <!-- Company -->
        <div class="mb-3">
          <label for="companyName" class="form-label">{{ $t('registrationForm.companyName') }} <span class="text-danger">*</span></label>
          <input id="companyName" type="text" class="form-control" v-model="companyData.companyName" required />
        </div>

        <div class="row gx-3">
          <div class="col-md-8 mb-3">
            <label for="companyStreetName" class="form-label">{{ $t('registrationForm.streetName') }} <span class="text-danger">*</span></label>
            <input id="companyStreetName" type="text" class="form-control" v-model="companyData.companyStreetName" required />
          </div>
          <div class="col-md-4 mb-3">
            <label for="companyStreetNumber" class="form-label">{{ $t('registrationForm.streetNumber') }} <span class="text-danger">*</span></label>
            <input id="companyStreetNumber" type="text" class="form-control" v-model="companyData.companyStreetNumber" required />
          </div>
        </div>

        <div class="row gx-3">
          <div class="col-md-6 mb-3">
            <label for="companyCity" class="form-label">{{ $t('registrationForm.city') }} <span class="text-danger">*</span></label>
            <input id="companyCity" type="text" class="form-control" v-model="companyData.companyCity" required />
          </div>
          <div class="col-md-6 mb-3">
            <label for="companyCountry" class="form-label">{{ $t('registrationForm.country') }} <span class="text-danger">*</span></label>
            <select id="companyCountry" class="form-select" v-model="companyData.companyCountry" @change="onCountryChange" required>
              <option value="" disabled>{{ $t('registrationForm.countryPlaceholder') }}</option>
              <option v-for="c in countries" :key="c.code" :value="c.name">{{ c.name }}</option>
            </select>
          </div>
        </div>

        <!-- Store language: suggested from the country (Netherlands -> Nederlands), never
             guessed for a country with more than one language (Belgium): then it's asked. -->
        <div class="row gx-3">
          <div class="col-md-6 mb-3">
            <label for="companyLanguage" class="form-label">{{ $t('registration.language') }} <span class="text-danger">*</span></label>
            <select id="companyLanguage" class="form-select" v-model="companyData.companyLanguage" required>
              <option value="" disabled>{{ $t('registration.languagePlaceholder') }}</option>
              <option v-for="code in LANGUAGES" :key="code" :value="code">{{ $t(`language.${code}`) }}</option>
            </select>
            <div class="form-text">{{ $t('registration.languageHelp') }}</div>
          </div>
        </div>

        <!-- Timezone: auto-set for single-tz countries; only asked when a country
             spans multiple timezones, so the store's tz is always unambiguous. -->
        <div class="row gx-3" v-if="countryTimezones.length > 1">
          <div class="col-md-6 mb-3">
            <label for="companyTimezone" class="form-label">{{ $t('registrationForm.timezone') }} <span class="text-danger">*</span></label>
            <select id="companyTimezone" class="form-select" v-model="companyData.companyTimezone" required>
              <option value="" disabled>{{ $t('registrationForm.timezonePlaceholder') }}</option>
              <option v-for="tz in countryTimezones" :key="tz" :value="tz">{{ tz }}</option>
            </select>
          </div>
        </div>

        <div class="row gx-3">
          <div class="col-md-6 mb-3">
            <label for="companyVATNumber" class="form-label">{{ $t('registrationForm.vatNumber') }} <span class="text-danger">*</span></label>
            <input id="companyVATNumber" type="text" class="form-control" v-model="companyData.companyVATNumber" required />
          </div>
          <div class="col-md-6 mb-3">
            <label for="companySubscriptionType" class="form-label">{{ $t('registrationForm.subscriptionType') }} <span class="text-danger">*</span></label>
            <input id="companySubscriptionType" type="text" class="form-control" v-model="companyData.companySubscriptionType" required />
          </div>
        </div>

        <div class="mb-3">
          <label for="companyDeviceURL" class="form-label">{{ $t('registrationForm.deviceUrl') }} <span class="text-danger">*</span></label>
          <input id="companyDeviceURL" type="text" class="form-control" v-model="companyData.companyDeviceURL" required />
        </div>

        <!-- Location & calendar: weather and holidays are part of every store -->
        <h5 class="section-title mt-4">{{ $t('registrationForm.locationCalendar') }}</h5>
        <hr class="section-rule">
        <location-calendar-fields
          v-model="locCal"
          :country-code="selectedCountry ? selectedCountry.code : ''"
          id-prefix="reg-loccal"
          @valid="locCalValid = $event"
        />

        <!-- Admin -->
        <h5 class="section-title mt-4">{{ $t('registrationForm.adminDetails') }}</h5>
        <hr class="section-rule">

        <div class="row gx-3">
          <div class="col-md-6 mb-3">
            <label for="userName" class="form-label">{{ $t('registrationForm.name') }} <span class="text-danger">*</span></label>
            <input id="userName" type="text" class="form-control" v-model="userData.userName" required />
          </div>
          <div class="col-md-6 mb-3">
            <label for="userSurname" class="form-label">{{ $t('registrationForm.surname') }} <span class="text-danger">*</span></label>
            <input id="userSurname" type="text" class="form-control" v-model="userData.userSurname" required />
          </div>
        </div>

        <div class="mb-3">
          <label for="userEmail" class="form-label">{{ $t('registrationForm.email') }} <span class="text-danger">*</span></label>
          <input
            id="userEmail"
            type="email"
            class="form-control"
            v-model="userData.userEmail"
            @blur="emailTouched = true"
            :class="{ 'is-invalid': emailTouched && !isEmailValid }"
            required
          />
          <div v-if="emailTouched && !isEmailValid" class="invalid-feedback">
            {{ $t('registrationForm.emailInvalid') }}
          </div>
        </div>

        <div v-if="loginError" class="error-message">{{ loginError }}</div>

        <button type="submit" class="stora-btn-primary w-100" :disabled="isButtonDisabled">
          {{ $t('registrationForm.submit') }}
        </button>

        <div v-if="isLoading" class="popup">
          <div class="loader"></div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'

import { useToast } from 'vue-toastification'

import image from "./icons/logo_storalytic.png"

import { useUserStore } from '@/stores/UserStore'
import { useCompanyStore } from '@/stores/CompanyStore'
import { useOpeningHoursStore } from '@/stores/OpeningHoursStore'
import { parseCoordinates } from '@/location/coordinates'
import LocationCalendarFields from '@/components/LocationCalendarFields.vue'
import { SUPPORTED as LANGUAGES, languageForCountry, i18n } from '@/i18n'

export default {
    name: 'Registration',

    components: { LocationCalendarFields },

    setup() {
        const router = useRouter()
                
        const userStore = useUserStore()
        const companyStore = useCompanyStore()
        const openingHoursStore = useOpeningHoursStore()

        const toast = useToast()

        const emailTouched = ref(false)
        const loginError = ref(null)

        const showNotification = ref(false)

        const isLoading = ref(null)

        const userData = ref({
            userName: '',
            userSurname: '',
            userEmail: '',
        })

        const companyData = ref({
            companyName: '',
            companyStreetName: '',
            companyStreetNumber: '',
            companyCity: '',
            companyCountry: '',
            companyTimezone: '',
            companyLanguage: '',
            companyVATNumber: '',
            companySubscriptionType: '',
            companyDeviceURL: ''
        })

        // Location & calendar (GPS -> weather; school region + sector calendars -> holidays)
        const emptyLocCal = () => ({ coordinates: '', school_region: null, school_region_type: null, sector: [] })
        const locCal = ref(emptyLocCal())
        const locCalValid = ref(false)

        // Country/timezone pickers — the list is authoritative (pytz, via /timezones),
        // so the store's country is canonical and its timezone unambiguous.
        const countries = ref([])
        const selectedCountry = computed(() =>
            countries.value.find(c => c.name === companyData.value.companyCountry) || null)
        const countryTimezones = computed(() => selectedCountry.value?.timezones || [])

        function onCountryChange() {
            const tzs = countryTimezones.value
            // Single-tz country -> set it automatically; multi-tz -> force an explicit pick.
            companyData.value.companyTimezone = tzs.length === 1 ? tzs[0] : ''
            companyData.value.companyLanguage = languageForCountry(selectedCountry.value?.code)
        }

        onMounted(async () => {
            try {
                const res = await fetch(`${import.meta.env.VITE_VUE_APP_API_URL}/timezones`)
                countries.value = await res.json()
            } catch (e) {
                console.error('Failed to load countries/timezones', e)
            }
        })

        // Computed property for email validation
        const isEmailValid = computed(() => {
            return /\S+@\S+\.\S+/.test(userData.value.userEmail)
        })

        // Computed property to determine if the button should be disabled
        const isButtonDisabled = computed(() => {
            const hasEmptyUserField = Object.values(userData.value).some(val => !val)
            const hasEmptyCompanyField = Object.values(companyData.value).some(val => !val)

            return hasEmptyUserField || hasEmptyCompanyField || !locCalValid.value
        })

        const goBack = () => router.push({ name: 'select-company' })

        const register = async () => {
            try {
                isLoading.value = true 

                // Step 1: Create company and get ID
                const coords = parseCoordinates(locCal.value.coordinates)
                const newCompanyId = await companyStore.registerCompany({
                    ...companyData.value,
                    companyLatitude: coords?.lat,
                    companyLongitude: coords?.lon,
                    companySchoolRegion: locCal.value.school_region,
                    companySchoolRegionType: locCal.value.school_region_type,
                    companySectorCalendars: locCal.value.sector,
                })
                
                // Step 2: Create (or link) the store admin.
                // PascalCase is not cosmetic — /add_user validates exactly these
                // key names. This form used to spread the camelCase form model
                // (userName/userEmail/companyId), so EVERY registration 400'd on
                // "CompanyId is required" after the company row was already
                // committed. Settings.vue's Add-user form had it right; this one
                // never did.
                const userPayload = {
                    UserName: userData.value.userName,
                    UserSurname: userData.value.userSurname,
                    UserEmail: userData.value.userEmail,
                    CompanyId: newCompanyId,
                    UserRole: 'Admin',
                }

                const addResult = await userStore.addUser(userPayload)

                await openingHoursStore.initializeDefaultOpeningHours(newCompanyId)

                console.log("Registration successful")

                // Reset form data after successful registration
                userData.value = {
                    userName: '',
                    userSurname: '',
                    userEmail: '',
                }

                companyData.value = {
                    companyName: '',
                    companyStreetName: '',
                    companyStreetNumber: '',
                    companyCity: '',
                    companyCountry: '',
                    companyTimezone: '',
                    companyLanguage: '',
                    companyVATNumber: '',
                    companySubscriptionType: '',
                    companyDeviceURL: ''
                }
                locCal.value = emptyLocCal()

                isLoading.value = false

                // The backend links instead of creating when the admin e-mail is
                // already on the platform (typically a Storalytic super-admin);
                // say so, because no set-password mail goes out in that case.
                toast.success(addResult?.linkedExistingUser
                    ? i18n.global.t('registrationForm.createdLinked')
                    : i18n.global.t('registrationForm.createdMailed'))

                // Back to the company picker, not /login: this screen is
                // super-admin-only (router guard), so whoever just created the
                // store is signed in already and wants to pick it. SelectCompany
                // re-fetches companyList onBeforeMount, so the new store is there.
                router.push({ name: 'select-company' })
            } catch (error) {
                isLoading.value = false
                console.error("Registration failed: ", error.message)
                loginError.value = i18n.global.t('registrationForm.failed', { error: error.message })
            }
        }

        return {
            LANGUAGES,
            logo_applicaite: image,
            userData,
            companyData,
            countries,
            selectedCountry,
            countryTimezones,
            locCal,
            locCalValid,
            onCountryChange,
            register,
            isEmailValid,
            emailTouched,
            loginError,
            isButtonDisabled,
            isLoading,
            showNotification,
            goBack
        }
    }
}
</script>

<style scoped>
.header-row {
  display:flex; align-items:center; justify-content:space-between; gap:12px;
}

.auth-head{
  display:flex; align-items:center; gap:10px; margin-bottom: 12px;
}
.auth-head .flex-1{ flex:1; }
.back-spacer{ width:82px; } /* keeps title centered */

.btn-outline{
  display:inline-flex; align-items:center; gap:8px;
  border:1px solid #4F7EB3; color:#4F7EB3; padding:6px 12px;
  border-radius:8px; text-decoration:none; background:#f7fbff;
}
.btn-outline:hover{ background:#4F7EB3; color:#fff }
.back-btn i{ font-size:1rem }

/* Brand primary */
.stora-btn-primary{
  display:inline-flex; align-items:center; justify-content:center;
  gap:8px; padding:10px 16px; border-radius:8px;
  background:#4F7EB3; color:#fff; border:0; cursor:pointer;
  transition: background .2s ease, transform .02s ease;
}
.stora-btn-primary:hover{ background:#B2C149; }
.stora-btn-primary:disabled{ opacity:.7; cursor:not-allowed; }

/* Sections */
.section-title { color:#425461; font-weight:700; margin-bottom:.25rem; }
.section-rule  { border-top:1px solid #e3e6ea; margin:0 0 1rem 0; }

/* Inputs */
.form-control{ max-width: 100%; }

/* Feedback / loader / popup / notification (re-use from your auth pages) */
.error-message{ margin-top: 12px; color:#C21500; }
.popup{
  position:fixed; inset:0; background:rgba(0,0,0,.4);
  display:flex; align-items:center; justify-content:center; z-index:1000;
}
.loader{
  width:80px; padding:8px; aspect-ratio:1; border-radius:50%;
  background:#B2C149;
  --_m:conic-gradient(#0000 10%,#000), linear-gradient(#000 0 0) content-box;
  -webkit-mask:var(--_m); mask:var(--_m);
  -webkit-mask-composite:source-out; mask-composite:subtract;
  animation:spinner 1s infinite linear;
}
@keyframes spinner{ to{ transform:rotate(1turn) } }

.notification{
  position:fixed; top:5%; left:50%; transform:translateX(-50%);
  padding:10px 20px; background:#d4edda; color:#155724;
  border:1px solid #c3e6cb; border-radius:5px; box-shadow:0 2px 4px rgba(0,0,0,.2);
  z-index:1000; text-align:center;
}
</style>